import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Composable for date formatting using date-fns
 * Replaces moment.js functionality with date-fns
 */
export function useDateFormat() {
  /**
   * Parse UTC date string and convert to local time
   * Equivalent to moment.utc(value).local()
   */
  const formatUtcToLocal = (value: string | Date | null | undefined, formatStr = 'PPpp'): string => {
    if (!value) return '';
    try {
      const date = typeof value === 'string' ? parseISO(value) : value;
      return format(date, formatStr, { locale: ptBR });
    } catch (error) {
      console.error('Error formatting UTC date:', error);
      return '';
    }
  };

  /**
   * Format a date with the specified format string
   * Equivalent to moment(value).format()
   */
  const formatDate = (value: string | Date | null | undefined, formatStr = 'PPpp'): string => {
    if (!value) return '';
    try {
      const date = typeof value === 'string' ? parseISO(value) : value;
      return format(date, formatStr, { locale: ptBR });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  /**
   * Format date in a human-readable long format
   * Equivalent to moment().format('LLL')
   * Example: "17 de novembro de 2025 às 21:30"
   */
  const formatHumanize = (value: string | Date | null | undefined): string => {
    if (!value) return '';
    try {
      const date = typeof value === 'string' ? parseISO(value) : value;
      // PPPp is equivalent to moment's LLL format
      return format(date, 'PPPp', { locale: ptBR });
    } catch (error) {
      console.error('Error humanizing date:', error);
      return '';
    }
  };

  /**
   * Format date as relative time from now
   * Equivalent to moment().fromNow()
   * Example: "há 2 horas", "em 3 dias"
   */
  const formatFromNow = (value: string | Date | null | undefined): string => {
    if (!value) return '';
    try {
      const date = typeof value === 'string' ? parseISO(value) : value;
      return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
    } catch (error) {
      console.error('Error formatting relative date:', error);
      return '';
    }
  };

  /**
   * Parse a date string or return a Date object
   * Equivalent to moment(value)
   */
  const parseDate = (value: string | Date | null | undefined): Date | null => {
    if (!value) return null;
    try {
      return typeof value === 'string' ? parseISO(value) : value;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  return {
    formatUtcToLocal,
    formatDate,
    formatHumanize,
    formatFromNow,
    parseDate,
  };
}

// Export standalone functions for use outside of components
export const {
  formatUtcToLocal,
  formatDate,
  formatHumanize,
  formatFromNow,
  parseDate
} = useDateFormat();
